<ng-container *ngIf="!cardMode; else cardTemplate">
  <ng-container *ngTemplateOutlet="content"></ng-container>
  <ng-container *ngIf="addThresholdFailed || addThresholdNoSuccess">
    <ng-container *ngTemplateOutlet="thresholdContent"></ng-container>
  </ng-container>
</ng-container>

<ng-template #cardTemplate>
  <div class="p-3">
    <h5 class="mb-1">{{ 'shared:notifications.recipients' | i18next }}</h5>
    <ng-container *ngIf="readOnly; else content">
      <mbs-text-ellipsis [tooltip]="allEmails">{{ allEmails }}</mbs-text-ellipsis>
    </ng-container>
  </div>
  <ng-container *ngIf="(addThresholdFailed || addThresholdNoSuccess) && (permissions?.threshold || !readOnly)">
    <hr class="m-0 opacity-100">
    <div class="p-3">
      <ng-container *ngIf="readOnly; else thresholdContent">
        <ng-container *ngTemplateOutlet="thresholdReadOnlyContent"></ng-container>
      </ng-container>
    </div>
  </ng-container>
</ng-template>

<ng-template #content>
  <mbs-checkbox [checked]="handleGetPermission(rule.Provider)"
                (change)="handleSetPermission(rule.Provider)"
                [label]="'shared:notifications.provider' | i18next"
                [id]="prefixId + '-notifications-provider'"
                [disabledSelf]="disabled">
    <ng-container appendLabel>
      <mbs-text-ellipsis class="small text-muted pl-3 align-self-center"
                         [tooltip]="providerEmail">({{ providerEmail }})</mbs-text-ellipsis>
    </ng-container>
  </mbs-checkbox>

  <mbs-checkbox [checked]="handleGetPermission(rule.SpecifiedEmails)"
                (change)="handleSetPermission(rule.SpecifiedEmails)"
                [label]="'shared:notifications.specifiedEmails' | i18next"
                [id]="prefixId + '-notifications-specified-emails'"
                [disabledSelf]="disabled"
                [descriptionOutside]="false"
                #specifiedEmails>
    <ng-container appendLabel>
      <mbs-button [isCtrl]="true"
                  class="ml-3"
                  [disabled]="disabled"
                  (click)="handleAddNew(specifiedEmailModal, permissions.emails)">
        {{ !!permissions?.emails?.length ? '(' + permissions.emails.length + ')' : '' }}
        {{ (!!permissions?.emails?.length ? 'shared:buttons.edit' : 'shared:buttons.add') | i18next }}
      </mbs-button>
    </ng-container>

    <!-- description of validation -->
    <ng-template checkboxDescription
                 [hide]="!(specifiedEmails.checked && permissions?.emails?.length === 0)">
      <mbs-input-errors [errors]="[{ message: validatorEmailsMessage }]"></mbs-input-errors>
    </ng-template>
  </mbs-checkbox>
</ng-template>

<ng-template #specifiedEmailModal>
  <div class="mbs-form_content">
    <div class="mbs-form-group">
      <mbs-list-editor [id]="prefixId + '-specifiedEmails-email-check-list'"
                       [showLabel]="false"
                       (ngModelChange)="handleListChange($event)"
                       entityName="specified email"
                       [(ngModel)]="editData"
                       [validators]="[emailValidator]">
      </mbs-list-editor>
    </div>
  </div>
</ng-template>

<ng-template #thresholdReadOnlyContent>
  <h5 class="mb-1">{{ 'shared:notifications.threshold' | i18next }}</h5>
  <div class="d-flex align-items-center">
    <span *ngIf="addThresholdFailed">
      {{ permissions?.threshold || thresholdMinValue }}&nbsp;{{ 'shared:notifications.consecutiveFailures' | i18next }}
    </span>
    <span *ngIf="!addThresholdFailed"
          class="ml-2">{{ thresholdUnitText }}</span>
    <span class="ico ico-info-circle cursor-help text-info ml-2"
          container="body"
          [placement]="cardMode ? 'left auto' : 'top auto'"
          [ngbTooltip]="(addThresholdFailed ? 'shared:notifications.failedThresholdTooltip' : 'shared:notifications.noSuccessThresholdTooltip') | i18next"></span>
  </div>
</ng-template>

<ng-template #thresholdContent>
  <div class="d-flex align-items-center justify-content-between"
       [ngClass]="{ 'panel mt-3 py-2 px-3': !cardMode }">
    <div class="d-flex align-items-center">
      <div *ngIf="addThresholdFailed && !cardMode"
           class="mr-2">
        <mbs-switcher [ngModel]="!isNil(permissions?.threshold)"
                      [disabledSelf]="!this.handleGetPermission(rule.Provider) &&
                  !this.handleGetPermission(rule.SpecifiedEmails)"
                      (change)="handleEnableThreshold($event)">
        </mbs-switcher>
      </div>
      <div>
        <span>{{ 'shared:notifications.useThreshold' | i18next }}</span>
      </div>
      <div class="threshold-number ml-2">
        <mbs-number [ngModel]="permissions?.threshold || thresholdMinValue"
                    (change)="handleSetThresholdValue($event)"
                    [autocomplete]="'off'"
                    [disabled]="!thresholdEnabled"
                    [min]="thresholdMinValue"
                    [max]="thresholdMaxValue"></mbs-number>
      </div>
      <div *ngIf="addThresholdNoSuccess"
           class="threshold-units ml-2">
        <mbs-select [items]="thresholdUnits"
                    [bindLabel]="'name'"
                    [bindValue]="'id'"
                    [disabled]="!thresholdEnabled"
                    [ngModel]="permissions?.thresholdUnit || 'days'"
                    [ngModelOptions]="{ standalone: true }"
                    [clearable]="false"
                    [searchable]="false"
                    (change)="handleSetThresholdUnit($event)"
                    [appendTo]="body"
                    [dropdownPosition]="'auto'"
                    [isAutoWidth]="false"></mbs-select>
      </div>
      <div *ngIf="addThresholdFailed"
           class="ml-2">
        <span>
          {{ 'shared:notifications.consecutiveFailures' | i18next }}
        </span>
      </div>
      <div class="ml-2">
        <span class="ico ico-info-circle cursor-help text-info"
              container="body"
              [placement]="cardMode ? 'left auto' : 'top auto'"
              [ngbTooltip]="thresholdTooltip()"></span>
      </div>
    </div>
    <div *ngIf="addThresholdFailed && cardMode">
      <mbs-switcher [ngModel]="!isNil(permissions?.threshold)"
                    [disabledSelf]="!this.handleGetPermission(rule.Provider) &&
                  !this.handleGetPermission(rule.SpecifiedEmails)"
                    (change)="handleEnableThreshold($event)">
      </mbs-switcher>
    </div>
  </div>
</ng-template>
